import { useState, useCallback, useRef, ChangeEvent, useMemo } from 'react'
import cn from 'classnames'

import PlainCheckbox from 'app/common/ui-next/plain-checkbox'
import Prompt from 'app/components/common/Prompt'
import usePopup from 'app/common/hooks/usePopup'
import SaveCancelButtons from 'app/components/common/SaveCancelButtons'
import { PageTemplateContent, Header2 } from 'app/common/templates-next/page-template'
import Sticky from 'app/common/templates-next/sticky'

import useReportsList from './useReportsList'
import styles from './SectionReports.module.scss'

const commonReports = [
  'food_cabinet_sales_report_use',
  'food_cabinet_subsidies_report_use',
  'food_cabinet_balances_report_use',
  'food_cabinet_accounting_report_use',
  'food_cabinet_purchases_by_funds_report_use',
  'food_cabinet_purchases_by_group_complexes_2_report_use',
  'food_cabinet_purchases_by_payment_mode_report_use',
  'food_cabinet_corrections_report_use',
  'food_cabinet_orders_by_classes_report_use',
  'food_cabinet_finances_summary_report_use',
  'food_cabinet_average_check_report_use',
]

const financesReports = [
  'food_cabinet_encashment_report_use',
  'food_cabinet_finances_e_money_legacy_report_use',
  'food_cabinet_finances_e_money_report_use',
  'food_cabinet_finances_by_sale_types_report_use',
  'food_cabinet_cafeteria_cash_report_use',
  'food_cabinet_finances_e_money_sales_report_use',
]

const desktopReports = [
  'desktop_client_daily_sales_report_use',
  'desktop_client_payment_mode_sales_report_use',
  'desktop_client_funds_sales_report_use',
  'desktop_client_daily_orders_report_use',
  'desktop_client_daily_distribution_report_use',
  'desktop_client_day_by_sale_type_report_use',
  'desktop_client_day_cash_report_use',
]

const optionLabels = {
  food_cabinet_sales_report_use: 'Продажи',
  food_cabinet_subsidies_report_use: 'Дотации',
  food_cabinet_balances_report_use: 'Остаток средств',
  food_cabinet_accounting_report_use: 'Учет выдачи',
  food_cabinet_purchases_by_funds_report_use: 'Продажи по видам средств',
  food_cabinet_purchases_by_group_complexes_2_report_use: 'Продажи по виду продукции',
  food_cabinet_purchases_by_payment_mode_report_use: 'Продажи по способу расчета',
  food_cabinet_corrections_report_use: 'Корректировки',
  food_cabinet_orders_by_classes_report_use: 'Учет количества питающихся',
  food_cabinet_finances_summary_report_use: 'Финансы',

  food_cabinet_encashment_report_use: 'Инкассация',
  food_cabinet_finances_e_money_legacy_report_use: 'ЭДС (версия 1.0)',
  food_cabinet_finances_e_money_report_use: 'ЭДС (версия 2.0)',
  food_cabinet_finances_by_sale_types_report_use: 'Финансы по типам продаж',
  food_cabinet_cafeteria_cash_report_use: 'Касса в столовой',
  food_cabinet_finances_e_money_sales_report_use: 'Продажи — ЭДС',

  desktop_client_daily_distribution_report_use: 'Выдано за день',
  desktop_client_daily_orders_report_use: 'Заказано на день',
  desktop_client_daily_sales_report_use: 'Продано за день',
  desktop_client_funds_sales_report_use: 'Продано по видам средств',
  desktop_client_payment_mode_sales_report_use: 'Продано по способу расчета',
  desktop_client_day_by_sale_type_report_use: 'Продано по типу продукции',
  desktop_client_day_cash_report_use: 'Продано наличными',
  food_cabinet_average_check_report_use: 'Средний чек',
}

type Props = {
  disabled?: boolean
}

const SectionReports = ({ disabled }: Props) => {
  const [{ reportsList, loading, modified }, { request, submit, update }] = useReportsList()
  // TODO reportsList тут содержит только достуаные для изменения (_enabled) отчеты, нужно сделать более явно
  // например получать такие только через параметр useReportsList
  const [saving, setSaving] = useState(false)
  const popup = usePopup()

  const handleSubmit = useCallback(
    async (e: ChangeEvent<HTMLFormElement>) => {
      e.preventDefault()
      setSaving(true)
      const res = await submit()
      setSaving(false)

      if (res) {
        popup('Настройки отчетов успешно сохранены')
      }
    },
    [submit, popup],
  )

  const formRef = useRef<HTMLFormElement | null>(null)
  const handleSave = useCallback(() => {
    if (formRef.current) {
      const submitButton = formRef.current.submit as unknown as HTMLButtonElement
      submitButton.click()
    }
  }, [])

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value, checked } = e.currentTarget
      update(value, checked)
    },
    [update],
  )

  const desktopReportsAvailable = useMemo(() => {
    return desktopReports.reduce(
      (acc, id) => {
        if (reportsList[id] !== undefined) {
          acc.push({ id, title: optionLabels[id] || `Неизвестный отчет (${id})` })
        }
        return acc
      },
      [] as Array<{ id: string; title: string }>,
    )
  }, [reportsList])

  return (
    <>
      <Sticky>
        {(ref, _, shadow) => (
          <PageTemplateContent horizontalLayout shadowBottom={shadow} reference={ref}>
            <Header2 skipMargin>{'Отчеты'}</Header2>
            {modified && !disabled && (
              <SaveCancelButtons
                onSave={handleSave}
                onCancel={request}
                disabled={loading || saving}
                saving={saving}
                cancelling={modified && loading}
              />
            )}
          </PageTemplateContent>
        )}
      </Sticky>

      <PageTemplateContent>
        <form onSubmit={handleSubmit} ref={formRef}>
          {/* TODO div нехорошо, нужно бы заменить на PlainCheckboxGroup */}
          <div className={styles.preText}>{'Выберите необходимые отчеты для раздела «Отчеты»'}</div>
          {commonReports.map(
            (id) =>
              reportsList[id] !== undefined && (
                <PlainCheckbox
                  key={id}
                  value={id}
                  label={optionLabels[id] || `Неизвестный отчет (${id})`}
                  disabled={loading || saving || disabled}
                  checked={reportsList[id]}
                  onChange={handleChange}
                />
              ),
          )}

          <div className={cn(styles.preText, styles.finances)}>
            {'Выберите необходимые отчеты для раздела «Финансы»'}
          </div>
          {financesReports.map(
            (id) =>
              reportsList[id] !== undefined && (
                <PlainCheckbox
                  key={id}
                  value={id}
                  label={optionLabels[id] || `Неизвестный отчет (${id})`}
                  disabled={loading || saving || disabled}
                  checked={reportsList[id]}
                  onChange={handleChange}
                />
              ),
          )}

          {desktopReportsAvailable.length > 0 && (
            <div className={cn(styles.preText, styles.finances)}>
              {
                'Выберите необходимые отчеты для настольного приложения «Инновационная школа — Клиент»'
              }
            </div>
          )}
          {desktopReportsAvailable.map(({ id, title }) => (
            <PlainCheckbox
              key={id}
              value={id}
              label={title}
              disabled={loading || saving || disabled}
              checked={reportsList[id]}
              onChange={handleChange}
            />
          ))}

          <button type="submit" name="submit" className={styles.hidden} disabled={disabled}>
            {'Сохранить'}
          </button>
        </form>

        <Prompt when={modified} message="Вы не сохранили изменения, перейти в любом случае?" />
      </PageTemplateContent>
    </>
  )
}

export default SectionReports
